import {
  BusinessGroupId,
  CMSMetadata,
  DeviceFamily,
  MarketingType,
  PageId,
  PageType,
  Placement,
  Provider,
  Slot,
} from '@amzn/fire-tv-campaign-automation-service';
import { createContext } from 'react';
import { CMSConfig, PAGE_TYPE_TO_PAGE_IDS } from 'src/configs/campaignMetadataConfig';
import { StateConstants } from 'src/constants';

export interface CampaignMetadataContextValue {
  metadata: CMSMetadata;
  updateMetadata: (updates: Partial<CMSMetadata>) => void;
}

/**
 * Device-family-specific default metadata values.
 * These are merged into the base defaults when creating a new campaign.
 */
const DEVICE_FAMILY_DEFAULT_METADATA: Partial<Record<DeviceFamily, Partial<CMSMetadata>>> = {
  [DeviceFamily.FIRE_TV]: {
    marketingType: MarketingType.DEALS_OFFERS_AND_PROMOTIONS,
  },
  [DeviceFamily.FIRE_TABLETS]: {
    marketingType: MarketingType.PRODUCT_DISCOVERY_AND_PRODUCT_LISTING,
  },
};

/**
 * Creates a default CMS metadata object with configurable dates
 * @param deviceFamily - Device family to determine default values
 * @param placement - Placement to determine default values
 * @param startDate - Optional start date for the campaign in epoch seconds (defaults to current time)
 * @param endDate - Optional end date for the campaign in epoch seconds (defaults to current time)
 * @returns A CMSMetadata object with default values
 */
export const getDefaultCMSMetadata = (
  deviceFamily: DeviceFamily,
  placement: Placement,
  startDate = Math.floor(Date.now() / 1000),
  endDate = Math.floor(Date.now() / 1000),
): CMSMetadata => ({
  accessibilityText: StateConstants.EMPTY_STRING,
  businessGroupId:
    CMSConfig?.BUSINESS_GROUP_IDS?.[deviceFamily]?.[placement]?.[0] ??
    BusinessGroupId.Bullseye_Users,
  campaignStartDate: startDate,
  campaignEndDate: endDate,
  clickThrough: StateConstants.EMPTY_STRING,
  pageType: CMSConfig?.PAGE_TYPES?.[deviceFamily]?.[placement]?.[0] ?? PageType.FTV_BROWSE,
  pageId:
    PAGE_TYPE_TO_PAGE_IDS[
      CMSConfig?.PAGE_TYPES?.[deviceFamily]?.[placement]?.[0] ?? PageType.FTV_BROWSE
    ]?.[0] ?? PageId.HOME_ROT_APPS,
  placement: placement,
  provider: Provider.CAPE,
  ...DEVICE_FAMILY_DEFAULT_METADATA[deviceFamily],
  slotName: Slot.GRID_1,
  title: StateConstants.EMPTY_STRING,
  locale: CMSConfig?.LOCALE?.[CMSConfig.MARKET_PLACE[0]]?.[0],
});

export const CampaignMetadataContext = createContext<CampaignMetadataContextValue>({
  metadata: getDefaultCMSMetadata(DeviceFamily.FIRE_TV, Placement.FEATURE_ROTATOR),
  updateMetadata: () => {},
});
